import React, { useEffect } from 'react';
import {
  CalculatorIcon,
  CloseIcon,
  CalendarIcon,
  CoinIcon,
  CopyIcon,
  CheckIcon,
} from './icons';
import './CalculadoraBCV.scss';

/** Fecha de hoy en formato ISO AAAA-MM-DD, usada como máximo seleccionable. */
const getTodayISO = (date = new Date()) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * CalculadoraBCVModal
 * ---------------------------------------------------------------------------
 * Componente de presentación (sin lógica de negocio). Recibe todo lo que
 * necesita por props desde `useCurrencyConverter`, lo que lo hace fácilmente
 * reutilizable y testeable de forma aislada.
 */
export const CalculadoraBCVModal = ({
  isOpen,
  onClose,
  paymentDate,
  onPaymentDateChange,
  rateFormatted,
  isLoadingRate,
  usdAmount,
  onUsdAmountChange,
  bsAmountFormatted,
  onCopy,
  copied,
}) => {
  // Cierra la modal con la tecla Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="bcv-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="bcv-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Calculadora BCV"
      >
        {/* Encabezado naranja */}
        <div className="bcv-modal__header">
          <span className="bcv-modal__header-icon">
            <CalculatorIcon />
          </span>
          <div className="bcv-modal__header-text">
            <h3 className="bcv-modal__title">Calculadora BCV</h3>
            <p className="bcv-modal__subtitle">
              Conversión USD → Bs. según tasa del día
            </p>
          </div>
          <button
            type="button"
            className="bcv-modal__close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="bcv-modal__body">
          {/* Fecha del pago (editable: determina la tasa a buscar) */}
          <div className="bcv-field">
            <label className="bcv-field__label">Fecha del pago</label>
            <div className="bcv-input bcv-input--editable">
              <span className="bcv-input__icon">
                <CalendarIcon />
              </span>
              <input
                className="bcv-input__control"
                type="date"
                value={paymentDate}
                onChange={onPaymentDateChange}
                max={getTodayISO()}
              />
            </div>
          </div>

          {/* Fila: Tasa BCV + Monto en USD */}
          <div className="bcv-row">
            {/* Tasa BCV (solo lectura) */}
            <div className="bcv-field">
              <label className="bcv-field__label">Tasa BCV (Bs/USD)</label>
              <div className="bcv-input bcv-input--readonly">
                <span className="bcv-input__icon">
                  <CoinIcon />
                </span>
                <input
                  className="bcv-input__control"
                  type="text"
                  value={isLoadingRate ? 'Cargando...' : rateFormatted}
                  readOnly
                />
              </div>
            </div>

            {/* Monto en USD (editable) */}
            <div className="bcv-field">
              <label className="bcv-field__label">Monto en USD ($)</label>
              <div className="bcv-input bcv-input--editable">
                <span className="bcv-input__symbol">$</span>
                <input
                  className="bcv-input__control"
                  type="text"
                  inputMode="decimal"
                  value={usdAmount}
                  onChange={onUsdAmountChange}
                  placeholder="0.00"
                />
                <span className="bcv-input__suffix">USD</span>
              </div>
            </div>
          </div>

          {/* Recuadro de resultado */}
          <div className="bcv-result">
            <div className="bcv-result__info">
              <p className="bcv-result__label">Monto en Bolívares</p>
              <p className="bcv-result__amount">Bs. {bsAmountFormatted}</p>
            </div>
            <button
              type="button"
              className={`bcv-result__copy${copied ? ' is-copied' : ''}`}
              onClick={onCopy}
              aria-label="Copiar monto"
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
              {copied && <span className="bcv-result__tooltip">¡Copiado!</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculadoraBCVModal;
